'use client'

import { useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { Menu, X, ChevronDown, Zap } from 'lucide-react'
import { ThemeLogo } from './theme-logo'
import { ThemeToggle } from '@/components/theme-toggle'

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null)
  const [mounted, setMounted] = useState(false)
  const [bannerDismissed, setBannerDismissed] = useState(false)
  const dropdownRef = useRef<HTMLUListElement>(null)

  useEffect(() => {
    setMounted(true)
  }, [])

  // Handle click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setActiveDropdown(null)
      }
    }

    // Handle scroll - close dropdown
    function handleScroll() {
      setActiveDropdown(null)
    }

    document.addEventListener('mousedown', handleClickOutside)
    window.addEventListener('scroll', handleScroll)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const navItems = [
    {
      title: 'Active Contracts',
      href: '/active-opportunities',
    },
    {
      title: 'Resources',
      dropdown: [
        { title: 'Features', href: '/features' },
        { title: 'Compare', href: '/compare' },
        { title: 'Documentation', href: '/documentation' },
        { title: 'Blog', href: '/blog' },
      ],
    },
    {
      title: 'Pricing',
      href: '/pricing',
    },
    {
      title: 'About',
      href: '/about',
    },
  ]

  // Toggle dropdown on click only
  const handleDropdownClick = (index: number) => {
    setActiveDropdown(activeDropdown === index ? null : index)
  }

  if (!mounted) {
    return (
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex h-20 items-center max-w-screen-2xl mx-auto">
          <div className="pl-4 md:pl-8">
            <ThemeLogo increasedSize={false} variant="words" />
          </div>
          <div className="flex-1" />
          <div className="flex items-center space-x-4 pr-4 md:pr-8">
            <Button asChild variant="outline" size="lg">
              <Link href="/login">Log In</Link>
            </Button>
            <Button asChild size="lg">
              <Link href="/signup">Free Trial</Link>
            </Button>
            <ThemeToggle />
          </div>
        </div>
      </header>
    )
  }

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 group">
      {!bannerDismissed && (
        <div className="relative bg-gradient-to-r from-sky-500 to-blue-600 text-white">
          <div className="flex items-center justify-center gap-2 px-4 py-1.5 text-xs sm:text-sm font-medium">
            <Zap className="w-3.5 h-3.5 flex-shrink-0" />
            <span>Over 100,000 Live Active Opportunities — <Link href="/active-opportunities" className="underline underline-offset-2 hover:text-white/90">Browse Now</Link></span>
            <button
              onClick={() => setBannerDismissed(true)}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-white/20 transition-colors"
              aria-label="Dismiss banner"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
      <div className="flex h-20 items-center max-w-screen-2xl mx-auto">
        <div className="pl-4 md:pl-8">
          <ThemeLogo increasedSize={false} variant="words" />
        </div>
        <div className="hidden lg:flex flex-1 items-center justify-center">
          <nav>
            <ul className="flex space-x-8" ref={dropdownRef}>
              {navItems.map((item, index) => (
                <li
                  key={index}
                  className="relative group"
                >
                  {item.dropdown ? (
                    <>
                      <button
                        className="flex items-center px-3 py-2 text-base font-semibold hover:text-primary transition-colors"
                        onClick={() => handleDropdownClick(index)}
                      >
                        {item.title}
                        <ChevronDown className={`ml-1 h-4 w-4 transition-transform duration-200 ${activeDropdown === index ? 'rotate-180' : ''}`} />
                      </button>
                      {activeDropdown === index && (
                        <div
                          className="absolute top-full left-0 mt-1 py-2 w-48 bg-background/95 border border-border rounded-md shadow-lg z-50 backdrop-blur-sm"
                        >
                          {item.dropdown.map((dropdownItem) => (
                            <Link
                              key={dropdownItem.title}
                              href={dropdownItem.href}
                              className="block px-4 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
                              onClick={() => setActiveDropdown(null)}
                            >
                              {dropdownItem.title}
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      href={item.href!}
                      className="flex items-center px-3 py-2 text-base font-semibold hover:text-primary transition-colors"
                    >
                      {item.title}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </nav>
        </div>
        <div className="hidden lg:flex items-center space-x-4 pr-4 md:pr-8">
          <Button asChild variant="outline" size="lg">
            <Link href="/login">Log In</Link>
          </Button>
          <Button asChild size="lg" className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 shadow-lg shadow-blue-500/30 text-white">
            <Link href="/signup">Free Trial</Link>
          </Button>
          <ThemeToggle />
        </div>

        {/* Mobile menu button */}
        <div className="flex flex-1 items-center justify-between space-x-2 lg:hidden">
          <div className="flex-1" />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="hover:bg-accent hover:text-accent-foreground active:scale-95 transition-transform"
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <div className={`lg:hidden fixed inset-x-0 ${bannerDismissed ? 'top-[81px]' : 'top-[113px]'} border-t border-border bg-background`}>
            <div className="container py-4">
              <nav className="mb-4">
                <ul className="space-y-2">
                  {navItems.map((item, index) => (
                    <li key={index}>
                      {item.dropdown ? (
                        <>
                          <span className="font-semibold text-base">{item.title}</span>
                          <ul className="ml-4 mt-1 space-y-1">
                            {item.dropdown.map((dropdownItem, dropdownIndex) => (
                              <li key={dropdownIndex}>
                                <Link
                                  href={dropdownItem.href}
                                  className="block py-1 text-sm hover:text-primary transition-colors"
                                >
                                  {dropdownItem.title}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </>
                      ) : (
                        <Link
                          href={item.href!}
                          className="block py-2 font-semibold text-base hover:text-primary transition-colors"
                        >
                          {item.title}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </nav>
              <div className="space-y-2">
                <Button asChild variant="outline" size="lg" className="w-full">
                  <Link href="/login">Log In</Link>
                </Button>
                <Button asChild size="lg" className="w-full">
                  <Link href="/signup">Free Trial</Link>
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  )
}